/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { ModalButton } from './ModalButton';
import { GeneralModal } from './GeneralModal';

const TEXTAREA_MAX_WIDTH = 450;
const TEXTAREA_HEIGHT = 180;

const MODAL_WIDTH = TEXTAREA_MAX_WIDTH + 40;

const textareaSx = {
  display: 'block',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: TEXTAREA_MAX_WIDTH,
  height: TEXTAREA_HEIGHT,
  resize: 'none',
  overflowY: 'auto',
  overflowX: 'hidden',
  mt: 2,
  mb: 4,
  p: 1.5,
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  font: 'inherit',
  fontSize: '0.9rem',
  lineHeight: 1.5,
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(128, 128, 128, 0.5) transparent',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(128, 128, 128, 0.5)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(128, 128, 128, 0.7)',
  },
  '&:focus': {
    outline: 'none',
    borderColor: 'text.secondary',
  },
};

export const EditTextModal = ({
  open,
  title,
  initialText,
  placeholder,
  maxLength,
  confirmLabel,
  cancelLabel,
  onCancel,
  onConfirm,
}) => {
  const [draft, setDraft] = useState(initialText);

  const trimmed = draft.trim();
  const canConfirm = !!trimmed;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(trimmed);
  };

  return (
    <GeneralModal
      open={open}
      onClose={onCancel}
      style={{ width: MODAL_WIDTH, px: 2.5 }}
    >
      <Typography id="modal-modal-title" variant="h6" component="h2">
        {title}
      </Typography>
      <Box
        component="textarea"
        aria-labelledby="modal-modal-title"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleConfirm();
          }
        }}
        placeholder={placeholder}
        maxLength={maxLength}
        sx={textareaSx}
      />
      <ModalButton
        primaryClickHandler={handleConfirm}
        secondaryClickHandler={onCancel}
        primaryBtnName={confirmLabel}
        secondaryBtnName={cancelLabel}
        disable={!canConfirm}
      />
    </GeneralModal>
  );
};

EditTextModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  initialText: PropTypes.string,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

EditTextModal.defaultProps = {
  title: 'Edit Text',
  initialText: '',
  placeholder: '',
  maxLength: undefined,
  confirmLabel: 'Save',
  cancelLabel: 'Cancel',
};
